/**
 * Ficha do clique num sistema de nuvens.
 *
 * Observado: cabeçalho com a situação e a tendência (intensificando ou não),
 * os números do quadro numa grade e a evolução em gráficos — área,
 * temperaturas mínima e média, expansão. Previsto: ficha curta, sem gráficos
 * (a previsão só desloca o contorno).
 *
 * No CELULAR a ficha é compacta (docs/proposta-ficha-rastreio-compacta.md): o
 * que se precisa saber em três segundos — qual sistema, a tendência, o
 * tamanho, o topo mais frio, para onde vai e há quanto tempo existe — e o
 * resto a um toque, em "Mais detalhes". No desktop ela é completa: há espaço,
 * e a ficha fica ao lado do sistema — a não ser quando a completa não cabe ao
 * lado dele sem cobri-lo: aí o `PopupHost` pede a compacta (`map/popupForma`).
 *
 * Os números vêm como o pyForTraCC os publica (K, km², km/h). Fonte e método
 * ficam no painel.
 *
 * A previsão do sistema (`PrevisaoDoSistema`, docs/rastreio-previsao-do-sistema.md):
 * um botão por antecedência, que leva o mapa a ela, e as sedes municipais que
 * o contorno previsto cobre. Vem do arquivo de previsão do quadro na tela, que
 * baixa só para a ficha quando a Previsão está desligada (`layer.ts`).
 */
import clsx from 'clsx';
import { ArrowUp, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { stepFrame } from '../loop/playback';
import { useLoopStore } from '../loop/store';
import { useGoesStore } from '../state/goes';
import { formatDecimal, formatInt } from '../lib/format';
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { useFormaDaFicha } from '../map/popupForma';
import { shownTimeOf, useTimeStore } from '../state/time';
import { Toggle } from '../ui/primitives';
import { useIsMobile } from '../ui/useMediaQuery';
import { PYFORTRACC_URL } from './api';
import { carregarSedes, cidadesNoCaminho, cidadesPorHorario, type CidadeNoCaminho, type Sede } from './caminho';
import { verPrevisao } from './fluxo';
import { motivos, NOME_CLASSE, serieRegular, tendencia, type Classe } from './evolucao';
import { IconeRastreio } from './icone';
import type { Sistema } from './layer';
import { ancoraDoSistema, chaveDoSistema, previsaoDoSistema, sistemaEm, ultimoRegistro, type PrevisaoDoSistema as Previsao } from './registro';
import { aoFecharFicha, chaveDoExterno, selecaoDe } from './selecao';
import { previsaoDesenhada, previsaoDoSelecionado, useTrackingStore } from './store';
import { quadroMaisNovoComPrevisao, quadroParaInstante, rotuloAntecedencia } from './tempo';
import { ALFA_DAS_FAIXAS_DO_CONE, comAlfa, COR_PREVISAO, COR_SELECAO } from './tracos';
import { horizonteDoCone } from './cone';
import { useConeStore } from './coneLigado';
import { Evolucao } from './TrackingChart';

const SITUACAO: Readonly<Record<string, string>> = {
  NEW: 'novo',
  CON: 'continua',
  SPL: 'divisão',
  MRG: 'fusão',
};

/** "SPL/MRG" → "divisão e fusão"; sigla desconhecida aparece como veio. */
export function nomeDaSituacao(st: string | null): string | null {
  if (!st) return null;
  return st
    .split('/')
    .map((s) => SITUACAO[s.trim()] ?? s.trim())
    .join(' e ');
}

const PONTOS = ['norte', 'nordeste', 'leste', 'sudeste', 'sul', 'sudoeste', 'oeste', 'noroeste'] as const;
const SIGLAS = ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO'] as const;

/** Rumo em graus → sigla do ponto cardeal ("L", "NE"), para onde o nome inteiro não cabe. */
export function siglaDoPonto(graus: number): string {
  return SIGLAS[Math.round((((graus % 360) + 360) % 360) / 45) % 8];
}

/** Rumo em graus a partir do norte (para onde vai) → ponto cardeal. */
export function pontoCardeal(graus: number): string {
  return PONTOS[Math.round((((graus % 360) + 360) % 360) / 45) % 8];
}

export function duracao(min: number): string {
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h} h ${String(m).padStart(2, '0')} min` : `${h} h`;
}

export function tituloDoSistema(s: Sistema): string {
  if (s.iuid !== null) return `Núcleo ${String(s.iuid)}`;
  return s.uid !== null ? `Sistema ${s.uid}` : 'Sistema';
}

const SELO: Readonly<Record<Classe, { icone: LucideIcon; classe: string }>> = {
  intensificando: { icone: TrendingUp, classe: 'border-red-400/50 bg-red-400/15 text-red-200' },
  estavel: { icone: Minus, classe: 'border-ink-500/60 bg-ink-700/60 text-ink-100' },
  enfraquecendo: { icone: TrendingDown, classe: 'border-rio-400/50 bg-rio-400/15 text-rio-300' },
  'sem-dados': { icone: Minus, classe: 'border-ink-600 bg-ink-800 text-ink-300' },
};

function Selo(props: { classe: Classe }) {
  const { icone: Icone, classe } = SELO[props.classe];
  return (
    <span
      data-tendencia={props.classe}
      className={clsx('inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-tight font-semibold', classe)}
    >
      <Icone className="size-3" aria-hidden />
      {NOME_CLASSE[props.classe]}
    </span>
  );
}

/** O desenho do cone, para o botão: a ponta à esquerda, o arco à direita. */
function IconeCone(props: { className?: string }) {
  return (
    <svg viewBox="0 0 12 12" className={props.className} aria-hidden>
      <path d="M1.2 6 L9.4 2.2 A4 4 0 0 1 9.4 9.8 Z" fill="currentColor" fillOpacity={0.3} stroke="currentColor" strokeWidth={1.3} strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Liga e desliga o cone de incerteza do sistema no mapa (`trajetoria.ts`,
 * pedido do usuário, 09/10/2026): um selo do tamanho do da tendência, na linha
 * do título da ficha compacta e da completa; o toque pega uma área maior que o
 * desenho (`before:-inset-2`).
 */
function BotaoDoCone() {
  const ligado = useConeStore((st) => st.ligado);
  return (
    <button
      type="button"
      aria-pressed={ligado}
      aria-label="Cone de previsão no mapa"
      title={ligado ? 'Ocultar o cone de previsão no mapa' : 'Mostrar o cone de previsão no mapa'}
      onClick={() => useConeStore.getState().setLigado(!ligado)}
      data-botao-cone=""
      className={clsx(
        "relative inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] leading-tight font-semibold transition-colors before:absolute before:-inset-2 before:content-['']",
        'focus-visible:outline-2 focus-visible:outline-mata-400',
        !ligado && 'border-ink-600 text-ink-400 hover:text-ink-200',
      )}
      style={ligado ? { borderColor: comAlfa(COR_PREVISAO, 0.55), background: comAlfa(COR_PREVISAO, 0.12), color: COR_PREVISAO } : undefined}
    >
      <IconeCone className="size-3" />
      Cone
    </button>
  );
}

/** Um número da grade: o rótulo pequeno em cima, o valor embaixo. */
function Dado(props: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[10px] leading-tight text-ink-400">{props.rotulo}</dt>
      <dd className="truncate text-xs leading-snug font-medium text-ink-100 tabular-nums">{props.children}</dd>
    </div>
  );
}

const kelvin = (v: number) => `${formatDecimal(v, 1)} K`;

function Cabecalho(props: { s: Sistema; abaixo: ReactNode; direita?: ReactNode }) {
  return (
    <div className="flex items-start gap-2 pr-5">
      <IconeRastreio className="mt-0.5 size-4 shrink-0 text-mata-400" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="leading-tight font-semibold text-white">{tituloDoSistema(props.s)}</div>
        <div className="text-[11px] leading-snug text-ink-300 tabular-nums">{props.abaixo}</div>
      </div>
      {props.direita}
    </div>
  );
}

function Deslocamento(props: { s: Sistema; rotulo: string }) {
  const { vel, rumo } = props.s;
  if (vel === null) return null;
  return (
    <Dado rotulo={props.rotulo}>
      <span className="inline-flex items-center gap-1">
        {rumo !== null && vel > 0 && (
          // O norte é para cima: a seta aponta para onde o sistema vai.
          <ArrowUp
            className="size-4 shrink-0 text-mata-300"
            strokeWidth={2.5}
            style={{ transform: `rotate(${rumo}deg)` }}
            aria-hidden
            data-seta-rumo={rumo}
          />
        )}
        {formatInt(vel)} km/h
        {/* A seta e a sigla dizem o rumo a quem vê (na célula estreita o nome não cabe); o nome inteiro, a quem ouve. */}
        {rumo !== null && vel > 0 && (
          <abbr title={`para ${pontoCardeal(rumo)}`} aria-label={`para ${pontoCardeal(rumo)}`} className="font-normal text-ink-300 no-underline">
            {siglaDoPonto(rumo)}
          </abbr>
        )}
      </span>
    </Dado>
  );
}

/** Um número da ficha compacta: rótulo e valor na mesma linha, sem cortar. */
function Chip(props: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1 text-xs leading-snug whitespace-nowrap">
      <dt className="text-[11px] text-ink-400">{props.rotulo}</dt>
      <dd className="font-medium text-ink-100 tabular-nums">{props.children}</dd>
    </div>
  );
}

/** A seta do rumo (o norte é para cima), a velocidade e o ponto cardeal por extenso. */
function Rumo(props: { s: Sistema }) {
  const { vel, rumo } = props.s;
  if (vel === null) return null;
  const anda = rumo !== null && vel > 0;
  return (
    <span className="inline-flex items-center gap-1">
      {anda && <ArrowUp className="size-3.5 shrink-0 text-mata-300" strokeWidth={2.5} style={{ transform: `rotate(${rumo}deg)` }} aria-hidden data-seta-rumo={rumo} />}
      {formatInt(vel)} km/h
      {anda && <span className="font-normal text-ink-300">{pontoCardeal(rumo)}</span>}
    </span>
  );
}

/**
 * "Mais detalhes": o que não é de três segundos, a um toque. No celular fica
 * como foi deixado enquanto a página está aberta. No desktop (a ficha ficou
 * compacta para caber ao lado do sistema) começa fechado a cada sistema, e o
 * `PopupHost` é avisado: aberta pelo usuário, a ficha pode cobrir o sistema.
 */
function Detalhes(props: { children: ReactNode; daSessao: boolean }) {
  const id = useId();
  const naSessao = useTrackingStore((st) => st.fichaDetalhes);
  const [aqui, setAqui] = useState(false);
  const { aoAbrirDetalhes } = useFormaDaFicha();
  const abertos = props.daSessao ? naSessao : aqui;
  useEffect(() => {
    aoAbrirDetalhes(abertos);
    return () => aoAbrirDetalhes(false);
  }, [abertos, aoAbrirDetalhes]);
  return (
    <div className="border-t border-white/10">
      <button
        type="button"
        aria-expanded={abertos}
        aria-controls={id}
        onClick={() => (props.daSessao ? useTrackingStore.getState().setFichaDetalhes(!abertos) : setAqui(!abertos))}
        className="flex min-h-9 w-full items-center gap-1 rounded-md text-left text-xs font-medium text-mata-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-11"
      >
        <span className="flex-1">{abertos ? 'Menos detalhes' : 'Mais detalhes'}</span>
        <ChevronDown className={clsx('size-4 transition-transform', abertos && 'rotate-180')} aria-hidden />
      </button>
      <div id={id} hidden={!abertos} className="space-y-1.5 pb-0.5" data-ficha-detalhes="">
        {abertos && props.children}
      </div>
    </div>
  );
}

/** O previsto compacto: deixa evidente que é previsão, e para que horário. */
function PrevistoCompacto(props: { s: Sistema; daSessao: boolean }) {
  const s = props.s;
  return (
    <div className="w-[min(16rem,72vw)] space-y-1" data-ficha-sistema="previsto" data-ficha-compacta="">
      <div className="flex items-center gap-1.5 pr-5 leading-tight">
        <IconeRastreio className="size-4 shrink-0 text-mata-400" aria-hidden />
        <span className="font-semibold text-white">{tituloDoSistema(s)}</span>
        <span className="rounded border border-yellow-300/40 bg-yellow-300/10 px-1.5 text-[11px] leading-4 font-medium text-yellow-200">Previsão</span>
      </div>
      {s.h !== null && (
        <div className="text-xs leading-snug font-medium text-yellow-200 tabular-nums">
          Para {formatTimeBrt(s.t + s.h * MINUTE_MS)} ({rotuloAntecedencia(s.h, { unidade: true })} depois das {formatTimeBrt(s.t)})
        </div>
      )}
      <dl className="flex flex-wrap gap-x-3 gap-y-0.5">
        {s.km2 !== null && <Chip rotulo="Área">{formatInt(s.km2)} km²</Chip>}
        {s.vel !== null && (
          <Chip rotulo="Vetor">
            <Rumo s={s} />
          </Chip>
        )}
      </dl>
      {(s.tmin !== null || s.tmed !== null) && (
        <Detalhes daSessao={props.daSessao}>
          <p className="text-[11px] leading-snug text-ink-300">A previsão desloca o contorno observado: os números são os da imagem das {formatTimeBrt(s.t)}.</p>
          <dl className="flex flex-wrap gap-x-3 gap-y-0.5">
            {s.tmin !== null && <Chip rotulo="Topo mais frio">{kelvin(s.tmin)}</Chip>}
            {s.tmed !== null && <Chip rotulo="Média">{kelvin(s.tmed)}</Chip>}
          </dl>
        </Detalhes>
      )}
    </div>
  );
}

function Previsto(props: { s: Sistema; compacta: boolean; daSessao: boolean }) {
  const s = props.s;
  if (props.compacta) return <PrevistoCompacto s={s} daSessao={props.daSessao} />;
  return (
    <div className="w-[min(15rem,70vw)] space-y-1.5" data-ficha-sistema="previsto">
      <div className="pr-5 text-xs text-ink-400">Previsão · pyForTraCC</div>
      <Cabecalho
        s={s}
        abaixo={
          s.h !== null ? (
            <span className="font-medium text-yellow-200">
              Previsão para {formatTimeBrt(s.t + s.h * MINUTE_MS)} ({rotuloAntecedencia(s.h, { unidade: true })})
            </span>
          ) : (
            'Previsão'
          )
        }
      />
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {s.km2 !== null && <Dado rotulo={s.lim !== null ? `Área abaixo de ${formatInt(s.lim)} K` : 'Área'}>{formatInt(s.km2)} km²</Dado>}
        <Deslocamento s={s} rotulo="Vetor usado" />
        {s.tmin !== null && <Dado rotulo="Topo mais frio">{kelvin(s.tmin)}</Dado>}
        {s.tmed !== null && <Dado rotulo="Média">{kelvin(s.tmed)}</Dado>}
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Previsão do sistema: os horários à frente e as cidades no caminho
// ---------------------------------------------------------------------------

type PrevisaoNaFicha = { estado: 'oculta' } | { estado: 'baixando' } | { estado: 'ok'; previsao: Previsao; chave: string };

/**
 * A previsão do sistema a partir do quadro na tela. Não aparece com o loop
 * tocando (a extrapolação é de um quadro só), no quadro sem previsão, nem
 * quando o arquivo chegou sem o sistema; enquanto ele baixa, só o título.
 */
function usePrevisaoNaFicha(s: Sistema): PrevisaoNaFicha {
  const indice = useTrackingStore((st) => st.indice);
  const versao = useTrackingStore((st) => st.versaoDoRegistro);
  const falhou = useTrackingStore((st) => st.noQuadro.previsao === 'falhou');
  const instante = useTimeStore((st) => shownTimeOf(st));
  const tocando = useLoopStore((st) => st.playing);
  const satelite = useGoesStore((st) => st.layers.some((l) => l.visible));
  return useMemo((): PrevisaoNaFicha => {
    void versao;
    const chave = chaveDoExterno(s, indice?.limiares ?? []);
    if (!indice || chave === null || tocando || !satelite) return { estado: 'oculta' };
    const q = quadroParaInstante(
      indice.quadros,
      indice.quadros.map((x) => x.t),
      instante,
    );
    if (!q?.prev) return { estado: 'oculta' };
    const previsao = previsaoDoSistema(chave, instante);
    if (!previsao) return falhou ? { estado: 'oculta' } : { estado: 'baixando' };
    return previsao.passos.length ? { estado: 'ok', previsao, chave } : { estado: 'oculta' };
  }, [s, indice, versao, falhou, instante, tocando, satelite]);
}

/** As sedes municipais, pedidas só quando há previsão para cruzar com elas. */
function useSedes(pedir: boolean): Sede[] | null {
  const [sedes, setSedes] = useState<Sede[] | null>(null);
  useEffect(() => {
    if (!pedir || sedes) return;
    let viva = true;
    void carregarSedes().then((x) => {
      if (viva && x) setSedes(x);
    });
    return () => {
      viva = false;
    };
  }, [pedir, sedes]);
  return sedes;
}

/** As cidades no caminho da previsão (null enquanto as sedes não chegaram). */
function useCidades(p: PrevisaoNaFicha): CidadeNoCaminho[] | null {
  const sedes = useSedes(p.estado === 'ok');
  return useMemo(() => {
    if (p.estado !== 'ok' || !sedes) return null;
    return cidadesNoCaminho(p.previsao.passos, ancoraDoSistema(p.chave, p.previsao.t)?.geometria ?? null, sedes);
  }, [p, sedes]);
}

const horaDe = (t: number, h: number) => formatTimeBrt(t + h * MINUTE_MS);

/**
 * Põe na tela a antecedência `h` deste sistema (0 = só a observação): na origem com a Previsão
 * ligada, a de todos; fora dela (ou desligada), só este sistema (`antecedenciaDaSelecao`).
 */
function porNaTela(h: number): void {
  const st = useTrackingStore.getState();
  if (st.previsao && st.naOrigem) {
    if (h > 0) verPrevisao(h);
    else st.setAntecedencia(0);
  } else st.setAntecedenciaDaSelecao(h > 0 ? h : null);
}

/**
 * Andar no tempo DESTE sistema, pelas setas da ficha (pedido do usuário, 08/10/2026):
 * - ‹ volta a antecedência (+1 h → +30 → a observação) e, na observação, a imagem anterior;
 * - › avança a imagem (a ficha acompanha o sistema) e, na última imagem rastreada — ou já com
 *   uma antecedência na tela —, a projeção dele: +30 … +3 h.
 */
function usePassosNoTempo(previsao: Previsao | null) {
  const satelite = useGoesStore((st) => st.layers.some((l) => l.visible));
  const naTela = useTrackingStore((st) => previsaoDesenhada(st, satelite) || previsaoDoSelecionado(st, satelite));
  const index = useTimeStore((st) => st.index);
  const frames = useTimeStore((st) => st.frames);
  const indice = useTrackingStore((st) => st.indice);
  const origem = indice ? quadroMaisNovoComPrevisao(frames, indice.quadros) : -1;
  const passos = previsao?.passos.map((p) => p.h) ?? [];
  const i = passos.indexOf(naTela);
  const naProjecao = naTela > 0;
  const projetar = naProjecao || (origem >= 0 ? index >= origem : index >= frames.length - 1);
  const voltar = () => {
    if (naProjecao) porNaTela(i > 0 ? passos[i - 1] : 0);
    else stepFrame(-1);
  };
  const avancar = () => {
    if (projetar) {
      const proxima = passos[i + 1];
      if (proxima !== undefined) porNaTela(proxima);
    } else stepFrame(1);
  };
  return {
    naTela,
    voltar,
    avancar,
    podeVoltar: naProjecao || index > 0,
    podeAvancar: projetar ? i + 1 < passos.length : index < frames.length - 1,
  };
}

function Seta(props: { lado: 'voltar' | 'avancar'; onClick: () => void; disabled: boolean }) {
  const Icone = props.lado === 'voltar' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.lado === 'voltar' ? 'Voltar no tempo (este sistema)' : 'Avançar no tempo (este sistema)'}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-md border border-ink-600/80 bg-ink-800/60 text-ink-100 transition-colors hover:border-ink-400 disabled:opacity-35 disabled:hover:border-ink-600/80',
        'min-h-9 w-7 self-stretch pointer-coarse:min-h-11 pointer-coarse:w-11',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
      )}
    >
      <Icone className="size-4" aria-hidden />
    </button>
  );
}

/**
 * Celular: o tempo deste sistema numa linha só — ‹ horário e o que é › —, para a previsão não
 * tomar a ficha (os seis horários e as cidades ocupavam a tela; pedido do usuário, 08/10/2026).
 */
function PassosCompactos(props: { previsao: Previsao | null; instante: number | null }) {
  const p = usePassosNoTempo(props.previsao);
  const t = props.previsao?.t ?? props.instante;
  if (t === null) return null;
  const projetado = p.naTela > 0;
  return (
    <div role="group" aria-label="Andar no tempo deste sistema" className="flex items-center gap-1.5" data-ficha-passos="">
      <Seta lado="voltar" onClick={p.voltar} disabled={!p.podeVoltar} />
      <div
        className="min-w-0 flex-1 rounded-md px-2 py-1 text-center leading-tight tabular-nums"
        style={projetado ? { backgroundColor: `${COR_SELECAO}22` } : undefined}
        aria-live="polite"
      >
        <span className="text-[12px] font-semibold text-white">{horaDe(t, p.naTela)}</span>{' '}
        <span className="text-[11px] text-ink-300">{projetado ? `previsto ${rotuloAntecedencia(p.naTela)}` : 'observado'}</span>
      </div>
      <Seta lado="avancar" onClick={p.avancar} disabled={!p.podeAvancar} />
    </div>
  );
}

/**
 * Um botão por antecedência: o horário em cima, a antecedência embaixo; o da
 * antecedência desenhada fica marcado. No celular, em três colunas: o alvo tem 44 px.
 * - Previsão desligada: projeta SÓ este sistema naquele horário, a partir da
 *   imagem na tela (`antecedenciaDaSelecao`) — os demais não; tocar de novo tira.
 * - Previsão ligada, na imagem de origem (a última rastreada): escolhe a antecedência de todos;
 *   numa imagem anterior, só este sistema, como com ela desligada.
 */
function HorariosDaPrevisao(props: { previsao: Previsao }) {
  const { t, passos } = props.previsao;
  const tempo = usePassosNoTempo(props.previsao);
  const naTela = tempo.naTela;
  // Tocar de novo no da tela volta à observação.
  const escolher = (h: number, ativo: boolean) => porNaTela(ativo ? 0 : h);
  return (
    <div className="flex items-stretch gap-1">
      <Seta lado="voltar" onClick={tempo.voltar} disabled={!tempo.podeVoltar} />
    {/* Três colunas: com as setas dos lados, seis em fila não cabem nos 18rem da ficha. */}
    <div role="group" aria-label="Ver a previsão no mapa" className="grid min-w-0 flex-1 gap-1" style={{ gridTemplateColumns: `repeat(${Math.min(3, passos.length)}, minmax(0, 1fr))` }}>
      {passos.map((p) => {
        const ativo = p.h === naTela;
        return (
          <button
            key={p.h}
            type="button"
            aria-pressed={ativo}
            aria-label={`${horaDe(t, p.h)}, ${rotuloAntecedencia(p.h, { unidade: true })}`}
            onClick={() => escolher(p.h, ativo)}
            className={clsx(
              'flex min-h-9 min-w-0 flex-col items-center justify-center rounded-md border leading-tight tabular-nums transition-colors pointer-coarse:min-h-11',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
              ativo ? 'border-transparent text-white' : 'border-ink-600/80 bg-ink-800/60 text-ink-100 hover:border-ink-400',
            )}
            style={ativo ? { backgroundColor: `${COR_SELECAO}33`, borderColor: COR_SELECAO } : undefined}
          >
            <span className="text-[11px] font-semibold">{horaDe(t, p.h)}</span>
            <span className="text-[10px] text-ink-300">{rotuloAntecedencia(p.h)}</span>
          </button>
        );
      })}
    </div>
      <Seta lado="avancar" onClick={tempo.avancar} disabled={!tempo.podeAvancar} />
    </div>
  );
}

/**
 * As cidades no caminho: uma linha por horário — "16:50 (+1 h) · Porto Velho,
 * Candeias do Jamari" —, até 5 nomes, e "e mais N".
 */
function CidadesNoCaminho(props: { cidades: CidadeNoCaminho[]; previsao: Previsao }) {
  const { t, passos } = props.previsao;
  const ultima = passos[passos.length - 1].h;
  if (!props.cidades.length) {
    return <p className="text-[11px] leading-snug text-ink-300">Nenhuma sede municipal no caminho até {rotuloAntecedencia(ultima, { unidade: true })}</p>;
  }
  const { grupos, mais } = cidadesPorHorario(props.cidades);
  return (
    <div>
      <div className="text-[10px] leading-tight text-ink-400">Cidades no caminho</div>
      <ul className="text-[11px] leading-snug text-ink-100">
        {grupos.map((g) => (
          <li key={g.h} className="tabular-nums">
            <span className="text-ink-300">{g.h === 0 ? 'Já sob o sistema' : `${horaDe(t, g.h)} (${rotuloAntecedencia(g.h)})`} · </span>
            {g.nomes.join(', ')}
          </li>
        ))}
        {mais > 0 && <li className="text-ink-400">e mais {formatInt(mais)}</li>}
      </ul>
    </div>
  );
}

/** Uma linha sobre o cone de incerteza no mapa (`trajetoria.ts`): a amostra ciano e até que horário ele vai. */
function LinhaDoCone(props: { previsao: Previsao; chave: string }) {
  const { previsao, chave } = props;
  const ligado = useConeStore((st) => st.ligado);
  const ate = horizonteDoCone(
    previsao.passos.map((p) => p.h),
    ancoraDoSistema(chave, previsao.t)?.trajeto ?? null,
  );
  if (ate <= 0 || !ligado) return null;
  return (
    <p className="flex items-center gap-1.5 text-[11px] leading-snug text-ink-300" data-ficha-cone="">
      <span className="inline-block h-2.5 w-4 shrink-0 rounded-sm border border-dashed" style={{ borderColor: COR_PREVISAO, background: comAlfa(COR_PREVISAO, ALFA_DAS_FAIXAS_DO_CONE[0]) }} aria-hidden />
      Área azul no mapa: por onde o centro deve passar até {formatTimeBrt(previsao.t + ate * MINUTE_MS)}
    </p>
  );
}

/** A previsão na ficha completa: título, horários, o cone e as cidades. */
function PrevisaoDoSistema(props: { p: PrevisaoNaFicha; cidades: CidadeNoCaminho[] | null }) {
  const { p } = props;
  if (p.estado === 'oculta') return null;
  return (
    <div className="space-y-1.5 border-t border-white/10 pt-1.5" data-ficha-previsao={p.estado}>
      <div className="text-[10px] leading-tight text-ink-400">{p.estado === 'baixando' ? 'Previsão…' : 'Previsão'}</div>
      {p.estado === 'ok' && (
        <>
          <HorariosDaPrevisao previsao={p.previsao} />
          <LinhaDoCone previsao={p.previsao} chave={p.chave} />
          {props.cidades && <CidadesNoCaminho cidades={props.cidades} previsao={p.previsao} />}
        </>
      )}
    </div>
  );
}

/** Na ficha compacta, uma linha: a primeira cidade no caminho e quantas mais. */
function CaminhoCompacto(props: { p: PrevisaoNaFicha; cidades: CidadeNoCaminho[] | null }) {
  const { p, cidades } = props;
  if (p.estado === 'oculta') return null;
  let texto: ReactNode = 'Previsão…';
  if (p.estado === 'ok' && cidades) {
    const ultima = p.previsao.passos[p.previsao.passos.length - 1].h;
    const [primeira] = cidades;
    texto = primeira ? (
      <>
        <span className="text-ink-400">No caminho:</span> {primeira.nome}{' '}
        {primeira.h === 0 ? <span className="text-ink-300">(já sob o sistema)</span> : horaDe(p.previsao.t, primeira.h)}
        {cidades.length > 1 && <span className="text-ink-300"> · +{formatInt(cidades.length - 1)}</span>}
      </>
    ) : (
      <span className="text-ink-300">No caminho: nenhuma sede até {rotuloAntecedencia(ultima, { unidade: true })}</span>
    );
  }
  return (
    <div className="text-[11px] leading-snug text-ink-100 tabular-nums" data-ficha-previsao={p.estado} data-ficha-caminho="">
      {texto}
    </div>
  );
}

/** Os núcleos do sistema no mapa: vale enquanto a ficha está aberta. */
function NucleosNoMapa(props: { uid: number; quantos: number }) {
  const ligado = useTrackingStore((st) => st.nucleos || st.nucleosDe === props.uid);
  const geral = useTrackingStore((st) => st.nucleos);
  useEffect(
    () => () => {
      const st = useTrackingStore.getState();
      if (st.nucleosDe === props.uid) st.setNucleosDe(null);
    },
    [props.uid],
  );
  return (
    <Toggle
      size="sm"
      label={props.quantos === 1 ? 'Mostrar o núcleo interno no mapa' : `Mostrar os ${formatInt(props.quantos)} núcleos internos no mapa`}
      checked={ligado}
      // Com a opção geral ligada (configurações do rastreio) todos os núcleos já estão no mapa.
      disabled={geral}
      onChange={(on) => useTrackingStore.getState().setNucleosDe(on ? props.uid : null)}
    />
  );
}

export function TrackingPopup(props: { sistema: Sistema }) {
  const clicado = props.sistema;
  // Compacta no celular, sempre; no desktop, quando a completa não cabe ao lado do sistema.
  const celular = useIsMobile();
  const { forma } = useFormaDaFicha();
  const compacta = celular || forma === 'compacta';
  // A ficha acompanha o player: os números são os do sistema no horário exibido.
  const instante = useTimeStore((st) => shownTimeOf(st));
  const versao = useTrackingStore((st) => st.versaoDoRegistro);
  // Com o loop tocando, as setas do tempo do sistema saem (o player é que anda).
  const tocando = useLoopStore((st) => st.playing);
  const chave = chaveDoSistema(clicado);
  const noQuadro = useMemo(() => {
    void versao;
    return clicado.previsto ? null : (sistemaEm(chave, instante) ?? (instante === clicado.t ? clicado : null));
  }, [chave, instante, versao, clicado]);
  // A história é a mais longa que se conhece: a do registro mais novo do sistema.
  const maisNovo = useMemo(() => {
    void versao;
    const r = clicado.previsto ? null : ultimoRegistro(chave);
    return r && r.t >= clicado.t ? r : clicado;
  }, [chave, versao, clicado]);
  // A ficha aberta é a seleção: o sistema dela fica destacado no mapa e na lista. A chave nas
  // dependências é o que troca o destaque quando a ficha passa de um sistema a outro (a instância
  // é reaproveitada); a limpeza só tira a seleção que é desta ficha.
  const uid = clicado.uid;
  useEffect(() => {
    const selecao = uid === null ? null : selecaoDe({ lim: clicado.lim, uid, iuid: clicado.iuid });
    useTrackingStore.getState().setSelecao(selecao);
    return () => {
      const st = useTrackingStore.getState();
      st.setSelecao(aoFecharFicha(st.selecao, selecao?.chave ?? null));
    };
    // `chave` resume `lim`, `uid` e `iuid`: é dela que o efeito depende.
  }, [chave]);
  const s = noQuadro ?? clicado;
  const prevista = usePrevisaoNaFicha(clicado);
  const cidades = useCidades(prevista);
  const t = useMemo(() => tendencia(s), [s]);
  const serie = useMemo(() => serieRegular(maisNovo.hist, maisNovo.t), [maisNovo]);
  if (clicado.previsto) return <Previsto key={chave} s={clicado} compacta={compacta} daSessao={celular} />;

  const situacao = nomeDaSituacao(s.st);
  const porque = motivos(t);
  const ausente = noQuadro === null && instante !== null;
  const nucleosNoMapa = s.iuid === null && s.uid !== null && s.nuc !== null && s.nuc > 0 ? <NucleosNoMapa uid={s.uid} quantos={s.nuc} /> : null;
  const fonte = (
    <a href={PYFORTRACC_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
      pyForTraCC
      <ExternalLink size={10} />
    </a>
  );
  if (compacta) {
    return (
      <div className="w-[min(16rem,72vw)] space-y-1" data-ficha-sistema="observado" data-ficha-compacta="">
        {/* Uma linha: qual sistema, de que horário, e a tendência. O fechar é do popup, ao lado. */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pr-5 leading-tight">
          <IconeRastreio className="size-4 shrink-0 text-mata-400" aria-hidden />
          <span className="font-semibold text-white">{tituloDoSistema(s)}</span>
          <span className="text-[11px] text-ink-300 tabular-nums">{formatTimeBrt(s.t)}</span>
          <Selo classe={t.classe} />
          {s.uid !== null && <BotaoDoCone />}
        </div>
        {ausente && (
          <div className="text-[11px] leading-snug text-yellow-200" data-ficha-ausente="">
            Sem este sistema na imagem das {formatTimeBrt(instante)}: os números são os das {formatTimeBrt(s.t)}.
          </div>
        )}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          {s.km2 !== null && <Chip rotulo="Área">{formatInt(s.km2)} km²</Chip>}
          {s.tmin !== null && <Chip rotulo="Topo">{kelvin(s.tmin)}</Chip>}
          {s.vel !== null && (
            <Chip rotulo="Vai">
              <Rumo s={s} />
            </Chip>
          )}
          {s.vida !== null && <Chip rotulo="Vida">{duracao(s.vida)}</Chip>}
        </dl>
        <CaminhoCompacto p={prevista} cidades={cidades} />
        {/* Celular: o tempo deste sistema numa linha, à vista; os seis horários e as cidades não. */}
        {celular && !tocando && <PassosCompactos previsao={prevista.estado === 'ok' ? prevista.previsao : null} instante={instante} />}
        <Detalhes key={chave} daSessao={celular}>
          {(situacao || porque.length > 0) && (
            <p className="text-[11px] leading-snug text-ink-300 tabular-nums">{[situacao && `Situação: ${situacao}`, ...porque].filter(Boolean).join(' · ')}</p>
          )}
          <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {s.tmed !== null && <Chip rotulo="Média">{kelvin(s.tmed)}</Chip>}
            {s.tmax !== null && <Chip rotulo="Mais quente">{kelvin(s.tmax)}</Chip>}
            {s.dp !== null && <Chip rotulo="Desvio">{kelvin(s.dp)}</Chip>}
            {s.iuid === null && s.nuc !== null && <Chip rotulo="Núcleos">{formatInt(s.nuc)}</Chip>}
            {s.exp !== null && (
              <Chip rotulo="Expansão">
                {s.exp > 0 ? '+' : s.exp < 0 ? '−' : ''}
                {formatInt(Math.abs(s.exp))}
                <span className="font-normal text-ink-400"> ×10⁻⁶/s</span>
              </Chip>
            )}
          </dl>
          {prevista.estado === 'ok' && !celular && (
            <div className="space-y-1.5" data-ficha-previsao-detalhes="">
              <HorariosDaPrevisao previsao={prevista.previsao} />
              <LinhaDoCone previsao={prevista.previsao} chave={prevista.chave} />
              {cidades && cidades.length > 0 && <CidadesNoCaminho cidades={cidades} previsao={prevista.previsao} />}
            </div>
          )}
          <div className="text-ink-200">
            {serie ? <Evolucao serie={serie} instante={instante} /> : <div className="text-[11px] text-ink-400">Sistema novo: ainda sem história.</div>}
          </div>
          {nucleosNoMapa}
          <div className="text-[11px] text-ink-400">{fonte}</div>
        </Detalhes>
      </div>
    );
  }
  return (
    <div className="w-[min(18rem,74vw)] space-y-1.5" data-ficha-sistema="observado">
      <div className="pr-5 text-xs text-ink-400">Sistema de nuvens · pyForTraCC</div>
      <Cabecalho
        s={s}
        abaixo={[formatTimeBrt(s.t), situacao, s.vida !== null ? `${duracao(s.vida)} de vida` : null].filter(Boolean).join(' · ')}
      />
      {ausente && (
        <div className="text-[11px] leading-snug text-yellow-200" data-ficha-ausente="">
          Sem este sistema na imagem das {formatTimeBrt(instante)}: os números são os das {formatTimeBrt(s.t)}.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Selo classe={t.classe} />
        {s.uid !== null && <BotaoDoCone />}
        {porque.length > 0 && <span className="text-[11px] leading-snug text-ink-300 tabular-nums">{porque.join(' · ')}</span>}
      </div>

      <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 border-t border-white/10 pt-1.5">
        {s.km2 !== null && <Dado rotulo={s.lim !== null ? `Área < ${formatInt(s.lim)} K` : 'Área'}>{formatInt(s.km2)} km²</Dado>}
        {s.tmin !== null && <Dado rotulo="Topo mais frio">{kelvin(s.tmin)}</Dado>}
        {s.tmed !== null && <Dado rotulo="Média">{kelvin(s.tmed)}</Dado>}
        <Deslocamento s={s} rotulo="Deslocamento" />
        {s.exp !== null && (
          <Dado rotulo="Expansão">
            {s.exp > 0 ? '+' : s.exp < 0 ? '−' : ''}
            {formatInt(Math.abs(s.exp))}
            <span className="font-normal text-ink-400"> ×10⁻⁶/s</span>
          </Dado>
        )}
        {s.iuid === null && s.nuc !== null && <Dado rotulo="Núcleos">{formatInt(s.nuc)}</Dado>}
        {s.tmax !== null && <Dado rotulo="Mais quente">{kelvin(s.tmax)}</Dado>}
        {s.dp !== null && <Dado rotulo="Desvio-padrão">{kelvin(s.dp)}</Dado>}
      </dl>

      <PrevisaoDoSistema p={prevista} cidades={cidades} />

      <div className="border-t border-white/10 pt-0.5 text-ink-200">
        {serie ? <Evolucao serie={serie} instante={instante} /> : <div className="pt-1 text-[11px] text-ink-400">Sistema novo: ainda sem história.</div>}
      </div>

      {nucleosNoMapa && <div className="border-t border-white/10 pt-1.5">{nucleosNoMapa}</div>}

      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">{fonte}</div>
    </div>
  );
}
